/**
 * Auth API - RTK Query endpoints for passwordless authentication
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
// IDN-14: sendTeamInvitation lives on this (authApi) slice, but the admin
// user list is served by a *separate* RTK Query slice (identityAdminApi).
// RTK Query only auto-invalidates tags within the same API slice, so a
// successful invite never refetched `TenantUsers` -- the invited (pending)
// user silently never appeared until an unrelated navigation/refetch. Import
// the other slice's `util.invalidateTags` action to cross the boundary.
import { identityAdminApi } from '../../identity/api/adminApi';

const DJANGO_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Custom base query with auth handling
const authBaseQuery = fetchBaseQuery({
  baseUrl: DJANGO_API_URL,
  credentials: 'include',
  prepareHeaders: (headers, { getState, endpoint }) => {
    headers.set('Content-Type', 'application/json');

    // List of endpoints that don't require authentication (signup flow + OTP login)
    const publicEndpoints = [
      'signupEmailRequest',
      'signupVerifyEmail',
      'signupCompanyDetails',
      'signupPlanSelection',
      'signupComplete',
      'requestMagicLink',
      'verifyMagicLink',
      'requestLoginOtp',
      'verifyLoginOtp',
      // IDN-21: passkey sign-in is pre-auth; never attach a stale token.
      'webAuthnLoginBegin',
      'webAuthnLoginComplete',
    ];

    // Only add auth token for endpoints that require it
    if (!publicEndpoints.includes(endpoint)) {
      const token = localStorage.getItem('access_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const tenantId = getState()?.auth?.tenantId || localStorage.getItem('tenant_id');
    if (tenantId) {
      headers.set('X-Tenant-ID', tenantId);
    }
    return headers;
  },
});

// Wrapper to handle token refresh on 401
const authBaseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await authBaseQuery(args, api, extraOptions);

  // Platform session length: {"detail": "Session expired."} means the sign-in
  // is past its limit. Refreshing cannot extend it, so treat it as terminal
  // exactly like shared/api/baseQuery.js: clear tokens/tenant, reset caches.
  if (
    result.error &&
    result.error.status === 401 &&
    result.error.data?.detail === 'Session expired.'
  ) {
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('tenant_id');
    } catch {
      /* localStorage unavailable */
    }
    try {
      sessionStorage.removeItem('tenant_id');
    } catch {
      /* sessionStorage unavailable */
    }
    const { resetAllApiState } = await import('../../../shared/api/resetApiState');
    resetAllApiState(api.dispatch);
    return result;
  }

  // If we got a 401, try to refresh the token
  if (result.error && result.error.status === 401) {
    const refreshToken = localStorage.getItem('refresh_token');

    if (refreshToken) {
      // Attempt to refresh the token
      const refreshResult = await authBaseQuery(
        {
          url: '/api/v1/auth/token/refresh/',
          method: 'POST',
          body: { refresh: refreshToken },
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        // Store the new tokens
        localStorage.setItem('access_token', refreshResult.data.access);
        if (refreshResult.data.refresh) {
          localStorage.setItem('refresh_token', refreshResult.data.refresh);
        }

        // Retry the original request with new token
        result = await authBaseQuery(args, api, extraOptions);
      } else {
        // Refresh failed - clear tokens
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('tenant_id');
      }
    }
  }

  return result;
};

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: authBaseQueryWithReauth,
  tagTypes: ['MyPasskeys', 'PlatformAuthSettings'],
  endpoints: (builder) => ({
    // Magic Link - Request
    requestMagicLink: builder.mutation({
      query: ({ email }) => ({
        url: '/api/v1/auth/magic-link/request/',
        method: 'POST',
        body: { email },
      }),
    }),

    // Magic Link - Verify
    verifyMagicLink: builder.mutation({
      query: ({ token }) => ({
        url: '/api/v1/auth/magic-link/verify/',
        method: 'POST',
        body: { token },
      }),
    }),

    // ============================================================================
    // Email OTP – Passwordless Login (primary auth method)
    // ============================================================================

    // Request a 6-digit OTP sent to the user's email
    requestLoginOtp: builder.mutation({
      query: ({ email }) => ({
        url: '/api/v1/auth/otp/request/',
        method: 'POST',
        body: { email },
      }),
    }),

    // Verify OTP and receive JWT tokens
    verifyLoginOtp: builder.mutation({
      query: ({ email, otp_code }) => ({
        url: '/api/v1/auth/otp/verify/',
        method: 'POST',
        body: { email, otp_code },
      }),
    }),

    // WebAuthn - Begin Registration
    webAuthnRegisterBegin: builder.mutation({
      query: () => ({
        url: '/api/v1/auth/webauthn/register/begin/',
        method: 'POST',
      }),
    }),

    // WebAuthn - Complete Registration
    webAuthnRegisterComplete: builder.mutation({
      query: (credential) => ({
        url: '/api/v1/auth/webauthn/register/complete/',
        method: 'POST',
        body: credential,
      }),
      // IDN-21: a passkey added from "My passkeys" must show up in the list.
      invalidatesTags: ['MyPasskeys'],
    }),

    // WebAuthn - Begin Login
    webAuthnLoginBegin: builder.mutation({
      query: ({ email }) => ({
        url: '/api/v1/auth/webauthn/login/begin/',
        method: 'POST',
        body: { email },
      }),
    }),

    // WebAuthn - Complete Login
    webAuthnLoginComplete: builder.mutation({
      query: ({ email, credential }) => ({
        url: '/api/v1/auth/webauthn/login/complete/',
        method: 'POST',
        body: { email, credential },
      }),
    }),

    // ============================================================================
    // Signup Endpoints
    // ============================================================================

    // Step 1: Email Verification Request
    signupEmailRequest: builder.mutation({
      query: ({ email }) => ({
        url: '/api/v1/auth/signup/email/',
        method: 'POST',
        body: { email },
      }),
    }),

    // Step 1b: Verify Email with Code
    signupVerifyEmail: builder.mutation({
      query: ({ email, verification_code }) => ({
        url: '/api/v1/auth/signup/verify-email/',
        method: 'POST',
        body: { email, verification_code },
      }),
    }),

    // Step 2: Company Details
    signupCompanyDetails: builder.mutation({
      query: ({ signup_token, company_name, company_website, industry, company_size }) => ({
        url: '/api/v1/auth/signup/company/',
        method: 'POST',
        body: {
          signup_token,
          company_name,
          company_website,
          industry,
          company_size,
        },
      }),
    }),

    // Step 3: Plan Selection
    signupPlanSelection: builder.mutation({
      query: ({ signup_token, plan, use_demo_data }) => ({
        url: '/api/v1/auth/signup/plan/',
        method: 'POST',
        body: { signup_token, plan, use_demo_data },
      }),
    }),

    // Step 4: Complete Signup (Admin Creation – no password required)
    signupComplete: builder.mutation({
      query: ({ signup_token, first_name, last_name, use_webauthn }) => ({
        url: '/api/v1/auth/signup/complete/',
        method: 'POST',
        body: {
          signup_token,
          first_name,
          last_name,
          use_webauthn,
        },
      }),
    }),

    // ============================================================================
    // IDN-21: My passkeys (the caller's own WebAuthn credentials only)
    // ============================================================================

    getMyPasskeys: builder.query({
      query: () => ({ url: '/api/v1/auth/users/credentials/', method: 'GET' }),
      // The endpoint returns a plain list; accept a paged/enveloped body too, so an
      // unexpected shape can't crash the profile page that renders the panel.
      transformResponse: (body) =>
        Array.isArray(body) ? body : (body?.results ?? body?.data ?? []),
      providesTags: ['MyPasskeys'],
    }),

    renamePasskey: builder.mutation({
      query: ({ id, name }) => ({
        url: `/api/v1/auth/webauthn/credentials/${id}/`,
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: ['MyPasskeys'],
    }),

    deletePasskey: builder.mutation({
      query: (id) => ({
        url: `/api/v1/auth/webauthn/credentials/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MyPasskeys'],
    }),

    // IDN-22: "Sign out everywhere" -- revokes all of the caller's own
    // refresh tokens server-side (no body: the target is always the caller).
    revokeAllSessions: builder.mutation({
      query: () => ({ url: '/api/v1/auth/token/revoke-all/', method: 'POST' }),
    }),

    // ============================================================================
    // Team Invitation Endpoints
    // ============================================================================

    // Send Team Member Invitation
    sendTeamInvitation: builder.mutation({
      query: ({ email, first_name, last_name, role_name }) => ({
        url: '/api/v1/auth/invitations/send/',
        method: 'POST',
        body: { email, first_name, last_name, role_name },
      }),
      // IDN-14: send_invitation (views_signup.py) creates a real pending
      // User row in the tenant -- invalidate identityAdminApi's TenantUsers
      // tag so any open user-role-administration list refetches and shows
      // the new pending invite instead of staying stale.
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(identityAdminApi.util.invalidateTags(['TenantUsers']));
        } catch {
          // Invite failed -- nothing to invalidate.
        }
      },
    }),

    // Accept Team Member Invitation (no password required)
    acceptTeamInvitation: builder.mutation({
      query: ({ invitation_token, first_name, last_name }) => ({
        url: '/api/v1/auth/invitations/accept/',
        method: 'POST',
        body: { invitation_token, first_name, last_name },
      }),
    }),

    // Revoke Team Member Invitation
    // IDN-23: previously removing an invitation from the onboarding UI only
    // dropped it from local Redux state -- the invitation row stayed
    // status='pending' server-side and the emailed link still worked. This
    // calls the real revoke endpoint (views_signup.py::revoke_invitation)
    // so the invitation is actually invalidated.
    revokeTeamInvitation: builder.mutation({
      query: (invitationId) => ({
        url: `/api/v1/auth/invitations/${invitationId}/revoke/`,
        method: 'POST',
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(identityAdminApi.util.invalidateTags(['TenantUsers']));
        } catch {
          // Revoke failed -- nothing to invalidate.
        }
      },
    }),

    // Onboarding - Seed Demo Data
    seedDemoData: builder.mutation({
      query: ({ tenant_id }) => ({
        url: `/api/v1/auth/onboarding/seed-demo-data/?tenant_id=${tenant_id}`,
        method: 'POST',
      }),
    }),

    // Onboarding - Get Status
    getOnboardingStatus: builder.query({
      query: ({ tenant_id }) => ({
        url: `/api/v1/auth/onboarding/status/?tenant_id=${tenant_id}`,
        method: 'GET',
      }),
      refetchOnMountOrArgChange: true,
    }),

    // Onboarding - Complete Onboarding
    completeOnboarding: builder.mutation({
      query: ({ tenant_id }) => ({
        url: `/api/v1/auth/onboarding/complete/?tenant_id=${tenant_id}`,
        method: 'PATCH',
      }),
    }),

    // MFA - Complete MFA challenge after password auth returned mfa_required=true
    completeMfa: builder.mutation({
      query: ({ partial_token, otp_code, webauthn_assertion }) => ({
        url: '/api/v1/auth/mfa/complete/',
        method: 'POST',
        body: { partial_token, otp_code, webauthn_assertion },
      }),
    }),

    // GDPR Data Subject Rights
    gdprExport: builder.query({
      query: () => ({ url: '/api/v1/auth/gdpr/export/', method: 'GET' }),
    }),
    // Platform sign-in length (staff only; IsAdminUser on the server).
    getPlatformAuthSettings: builder.query({
      query: () => ({ url: '/api/v1/auth/platform-settings/', method: 'GET' }),
      providesTags: ['PlatformAuthSettings'],
    }),
    updatePlatformAuthSettings: builder.mutation({
      query: (body) => ({ url: '/api/v1/auth/platform-settings/', method: 'PUT', body }),
      invalidatesTags: ['PlatformAuthSettings'],
    }),
    gdprErasure: builder.mutation({
      query: () => ({
        url: '/api/v1/auth/gdpr/erasure/',
        method: 'POST',
        body: { confirm_erasure: true },
      }),
    }),
  }),
});

export const {
  useGetPlatformAuthSettingsQuery,
  useUpdatePlatformAuthSettingsMutation,
  useRequestMagicLinkMutation,
  useVerifyMagicLinkMutation,
  useRequestLoginOtpMutation,
  useVerifyLoginOtpMutation,
  useWebAuthnRegisterBeginMutation,
  useWebAuthnRegisterCompleteMutation,
  useWebAuthnLoginBeginMutation,
  useWebAuthnLoginCompleteMutation,
  useGetMyPasskeysQuery,
  useRenamePasskeyMutation,
  useDeletePasskeyMutation,
  useRevokeAllSessionsMutation,
  useSignupEmailRequestMutation,
  useSignupVerifyEmailMutation,
  useSignupCompanyDetailsMutation,
  useSignupPlanSelectionMutation,
  useSignupCompleteMutation,
  useSendTeamInvitationMutation,
  useAcceptTeamInvitationMutation,
  useRevokeTeamInvitationMutation,
  useSeedDemoDataMutation,
  useGetOnboardingStatusQuery,
  useCompleteOnboardingMutation,
  useCompleteMfaMutation,
  useLazyGdprExportQuery,
  useGdprErasureMutation,
} = authApi;
